import React from "react";
import { GlobalStyle } from "./Constant/global";
import styled, { keyframes } from "styled-components";
import logoImage from "./Assets/codeykt-logo.png";
import bigLogoImage from "./Assets/biglogo.png";
import BeamsBackground from "./Components/BeamsBackground/BeamsBackground";

const Container = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  background-color: transparent;
  padding: 2rem;
  box-sizing: border-box;
  cursor: pointer;

  @media (max-width: 1024px) {
    padding: 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    min-height: 100vh;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const rotateText = keyframes`
  0% {
    transform: rotateX(90deg);
    opacity: 0;
  }
  10%, 90% {
    transform: rotateX(0deg);
    opacity: 1;
  }
  100% {
    transform: rotateX(-90deg);
    opacity: 0;
  }
`;

const LiveTextContainer = styled.div`
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 70px;
  color: #ffffff;
  text-align: center;
  margin-bottom: 2rem;
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 1.2em;
  perspective: 1000px;
  gap: 0.3em;

  @media (max-width: 1024px) {
    font-size: 60px;
  }

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    font-size: 50px;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
    font-size: 40px;
  }

  @media (max-width: 320px) {
    font-size: 35px;
  }
`;

const RotatingWord = styled.span`
  display: inline-block;
  animation: ${rotateText} 12s infinite;
  animation-delay: ${(props) => props.delay || 0}s;
  transform-style: preserve-3d;
  backface-visibility: hidden;
  transform-origin: center bottom;
`;

const LiveText = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
`;

const HeroText = styled.h1`
  display: flex;
  align-items: center;
  gap: 20px;
  margin: 0;
  font-family: "Poppins", sans-serif;
  font-weight: 800;
  line-height: 1.2;
  font-size: clamp(2rem, 8vw, 4rem);
  color: #ffffff;
  text-align: center;
  flex-wrap: wrap;
  justify-content: center;

  span {
    color: #ffffff;
    white-space: nowrap;
  }

  @media (max-width: 1024px) {
    gap: 18px;
    font-size: clamp(1.75rem, 7vw, 3.5rem);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    font-size: clamp(1.5rem, 6vw, 2.5rem);
  }

  @media (max-width: 480px) {
    gap: 10px;
    font-size: clamp(1.25rem, 5vw, 2rem);
  }

  @media (max-width: 320px) {
    font-size: clamp(1rem, 4vw, 1.75rem);
  }
`;

const arrowMove = keyframes`
  0%, 100% {
    transform: translateX(0);
    opacity: 1;
  }
  50% {
    transform: translateX(15px);
    opacity: 0.7;
  }
`;

const metallicShine = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const ArrowChevron = styled.span`
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 50px solid #ffffff;
  border-top: 30px solid transparent;
  border-bottom: 30px solid transparent;
  position: relative;
  animation: ${arrowMove} 1.8s ease-in-out infinite;
  filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.6)) 
          drop-shadow(0 0 20px rgba(200, 200, 255, 0.4))
          drop-shadow(0 0 5px rgba(150, 150, 255, 0.3));
  
  &::before {
    content: '';
    position: absolute;
    left: -50px;
    top: -30px;
    width: 50px;
    height: 60px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.4) 20%,
      rgba(255, 255, 255, 0.9) 40%,
      rgba(200, 220, 255, 1) 50%,
      rgba(255, 255, 255, 0.9) 60%,
      rgba(255, 255, 255, 0.4) 80%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: ${metallicShine} 2.5s linear infinite;
    clip-path: polygon(0 0, 100% 50%, 0 100%);
    z-index: 1;
    pointer-events: none;
    mix-blend-mode: overlay;
  }
  
  @media (max-width: 1024px) {
    &::before {
      left: -40px;
      top: -25px;
      width: 40px;
      height: 50px;
    }
  }

  @media (max-width: 768px) {
    &::before {
      left: -35px;
      top: -20px;
      width: 35px;
      height: 40px;
    }
  }

  @media (max-width: 480px) {
    &::before {
      left: -28px;
      top: -16px;
      width: 28px;
      height: 32px;
    }
  }

  @media (max-width: 320px) {
    &::before {
      left: -22px;
      top: -13px;
      width: 22px;
      height: 26px;
    }
  }
  
  &:nth-child(1) {
    animation-delay: 0s;
  }
  
  &:nth-child(2) {
    animation-delay: 0.3s;
  }
  
  &:nth-child(3) {
    animation-delay: 0.6s;
  }

  @media (max-width: 1024px) {
    border-left-width: 40px;
    border-top-width: 25px;
    border-bottom-width: 25px;
  }

  @media (max-width: 768px) {
    border-left-width: 35px;
    border-top-width: 20px;
    border-bottom-width: 20px;
  }

  @media (max-width: 480px) {
    border-left-width: 28px;
    border-top-width: 16px;
    border-bottom-width: 16px;
  }

  @media (max-width: 320px) {
    border-left-width: 22px;
    border-top-width: 13px;
    border-bottom-width: 13px;
  }
`;

const AnimatedArrow = styled.div`
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 180px;
  height: 80px;
  gap: 10px;

  @media (max-width: 1024px) {
    width: 150px;
    height: 65px;
    gap: 8px;
  }

  @media (max-width: 768px) {
    width: 120px;
    height: 50px;
    gap: 6px;
  }

  @media (max-width: 480px) {
    width: 90px;
    height: 40px;
    gap: 5px;
  }

  @media (max-width: 320px) {
    width: 75px;
    height: 35px;
    gap: 4px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    gap: 10px;
  }

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const LogoImage = styled.img`
  height: clamp(100px, 12vw, 180px);
  width: auto;
  object-fit: contain;
  display: block;

  @media (max-width: 1024px) {
    height: clamp(90px, 11vw, 160px);
  }

  @media (max-width: 768px) {
    height: clamp(80px, 10vw, 140px);
  }

  @media (max-width: 480px) {
    height: clamp(70px, 9vw, 120px);
  }

  @media (max-width: 320px) {
    height: clamp(60px, 8vw, 100px);
  }
`;

const BigLogoImage = styled.img`
  height: clamp(120px, 15vw, 250px);
  width: auto;
  object-fit: contain;
  display: block;

  @media (max-width: 1024px) {
    height: clamp(110px, 14vw, 220px);
  }

  @media (max-width: 768px) {
    height: clamp(100px, 13vw, 200px);
  }

  @media (max-width: 480px) {
    height: clamp(90px, 12vw, 170px);
  }

  @media (max-width: 320px) {
    height: clamp(80px, 11vw, 150px);
  }
`;

const App = () => {
  const handleClick = () => {
    window.open("https://www.codeyukt.com/", "_blank");
  };

  return (
    <>
      <GlobalStyle />
      <BeamsBackground />
      <Container onClick={handleClick}>
        <LiveTextContainer>
          <LiveText>
            <RotatingWord delay={0}>we</RotatingWord>{" "}
            <RotatingWord delay={3}>are</RotatingWord>{" "}
            <RotatingWord delay={6}>now</RotatingWord>{" "}
            <RotatingWord delay={9}>live</RotatingWord>
          </LiveText>
        </LiveTextContainer>
        <HeroText>
          <BigLogoImage src={bigLogoImage} alt="Devetree Logo" />
          <AnimatedArrow>
            <ArrowChevron />
            <ArrowChevron />
            <ArrowChevron />
          </AnimatedArrow>
          <LogoContainer>
            <LogoImage src={logoImage} alt="Codeyukt Logo" />
          </LogoContainer>
        </HeroText>
      </Container>
    </>
  );
};

export default App;
